// Выбор исполнителей — один на карточку задачи и быструю форму: выбранные чипами с кружком и ×, поле
// «Add person…» с подсказками при вводе, команды чипами (клик подставляет состав), по желанию — решётка
// всех людей чекбоксами (карточка; раскрытие помнится как личная настройка).
import { Fragment, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { useTable } from '@core/store';
import { activePeople, rankByQuery } from '@core/people';
import { Avatar } from '@core/ui/Avatar';
import { POPOVER, cn } from '@core/ui/cn';
import { ChipGhostMark, useChipReorder } from '@core/ui/ChipReorder';
import { MAX_ASSIGNEES } from './data';

export type PickPerson = { id: string; name: string; color: string };
export type PickTeam = { id: string; name: string; members: string[] };

/** Активные люди по алфавиту — кого можно назначить. */
export function usePeopleList(): PickPerson[] {
  const people = useTable('person');
  return useMemo(() => activePeople(people.values()).map((p) => ({ id: p.id, name: p.name, color: p.color })), [people]);
}

/** Команды с активным составом, в порядке админки. Состав правится в админке. */
export function useTeamList(): PickTeam[] {
  const people = useTable('person');
  const teams = useTable('team');
  const teamMembers = useTable('team_member');
  return useMemo(() => [...teams.values()].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)).map((t) => ({
    id: t.id, name: t.name,
    members: [...teamMembers.values()].filter((m) => m.team_id === t.id).sort((a, b) => a.position - b.position).map((m) => m.person_id).filter((id) => people.get(id)?.is_active),
  })).filter((t) => t.members.length > 0), [teams, teamMembers, people]);
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));
const SUGGEST = 6;

type Props = {
  value: string[];
  onChange: (ids: string[]) => void;
  people: PickPerson[];
  teams: PickTeam[];
  /** Решётка всех людей чекбоксами: undefined — без неё (быстрая форма); иначе состояние и переключатель. */
  grid?: { open: boolean; onToggle: (open: boolean) => void };
  onEnterEmpty?: () => void; // Enter в пустом поле без подсказки — отдать наверх (быстрая форма создаёт задачу)
};

export function AssigneePicker({ value, onChange, people, teams, grid, onEnterEmpty }: Props) {
  const max = MAX_ASSIGNEES;
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const full = value.length >= max;
  const q = query.trim().toLowerCase();
  const suggestions = useMemo(() => rankByQuery(people.filter((p) => !value.includes(p.id)), q).slice(0, SUGGEST), [q, people, value]);
  const hit = suggestions[Math.min(active, suggestions.length - 1)];

  const { chipProps, ghost } = useChipReorder(value, onChange); // порядок чипов = порядок полос на баре
  const ghostMark = ghost ? <ChipGhostMark label={byId.get(ghost.id)?.name ?? ''} className="h-8 text-sm font-medium" /> : null;
  const add = (id: string) => { if (!value.includes(id) && !full) onChange([...value, id]); setQuery(''); setActive(0); setOpen(false); inputRef.current?.focus(); };
  const remove = (id: string) => onChange(value.filter((x) => x !== id));
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, suggestions.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Escape' && (open || query)) { e.preventDefault(); e.stopPropagation(); setQuery(''); setOpen(false); return; }
    if (e.key === 'Backspace' && !query && value.length) { onChange(value.slice(0, -1)); return; }
    if (e.key === 'Enter' || (e.key === 'Tab' && hit && query)) {
      if (hit && query) { e.preventDefault(); add(hit.id); return; }
      if (e.key === 'Enter') { e.preventDefault(); if (!query) onEnterEmpty?.(); }
    }
  };

  return (
    <div className="flex flex-col gap-2" data-assignee-picker data-keeps-escape={(open && suggestions.length > 0) || query ? '' : undefined}>
      <div className="flex items-center gap-1.5 flex-wrap min-h-11 rounded-md border border-line bg-paper px-2 py-1">
        {value.map((id) => {
          const p = byId.get(id);
          if (!p) return null;
          return (<Fragment key={id}>
            {ghost?.before === id && ghostMark}
            <span data-assignee-chip={id} {...chipProps(id)} className={cn('inline-flex items-center gap-1.5 h-8 pl-1 pr-1.5 rounded-full bg-gray-100 border border-gray-200 text-sm font-medium', chipProps(id).className)}>
              <Avatar name={p.name} color={p.color} size={22} />
              {p.name}
              <button type="button" className="min-h-0 w-5 h-5 rounded-full inline-flex items-center justify-center text-gray-500 hover:bg-gray-300 hover:text-gray-900" aria-label={`Remove ${p.name}`} onClick={() => remove(id)}><X className="w-3.5 h-3.5" /></button>
            </span>
          </Fragment>);
        })}
        {ghost?.before === null && ghostMark}
        <span className="relative flex-1 min-w-32">
          <input
            ref={inputRef} data-assignee-input value={query} disabled={full} autoComplete="off"
            className="min-h-0 h-8 w-full bg-transparent outline-none text-base placeholder:text-gray-400 disabled:placeholder:text-gray-300"
            placeholder={full ? `Up to ${max}` : value.length ? 'Add…' : 'Add person…'} aria-label="Add person"
            onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)} onKeyDown={onKey}
          />
          {open && suggestions.length > 0 && (
            <ul role="listbox" className={cn('absolute left-0 top-full mt-1 z-50 min-w-48', POPOVER)}>
              {suggestions.map((p, i) => (
                <li key={p.id} role="option" aria-selected={p.id === hit?.id} data-assignee-option={p.id}
                  className={cn('flex items-center gap-2 px-2 py-1.5 rounded cursor-default', p.id === hit?.id && 'bg-gray-100')}
                  onMouseDown={(e) => { e.preventDefault(); add(p.id); }} onMouseEnter={() => setActive(i)}>
                  <Avatar name={p.name} color={p.color} size={20} />{p.name}
                </li>
              ))}
            </ul>
          )}
        </span>
      </div>
      {teams.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Teams">
          <span className="text-sm text-gray-600 mr-0.5">Teams:</span>
          {teams.map((t) => {
            const on = sameSet(value, t.members);
            return (
              <button key={t.id} type="button" aria-pressed={on} data-team-chip={t.id}
                className={cn('min-h-0 h-7 px-2 rounded-md border text-sm', on ? 'bg-gray-200 border-gray-400 font-semibold' : 'bg-white border-gray-300 hover:bg-gray-100')}
                title={t.members.map((id) => byId.get(id)?.name ?? '').join(', ')}
                onClick={() => { onChange(t.members.slice(0, max)); inputRef.current?.focus(); }}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}
      {grid && (
        <>
          <button type="button" className="min-h-0 self-start text-sm text-accent hover:underline" data-assignee-grid-toggle onClick={() => grid.onToggle(!grid.open)}>
            {grid.open ? 'Hide the list' : `Show all ${people.length}`}
          </button>
          {grid.open && (
            <div className="grid grid-cols-3 gap-x-4 gap-y-1" data-assignee-grid>
              {people.map((p) => {
                const on = value.includes(p.id);
                const disabled = !on && full;
                return (
                  <label key={p.id} className={cn('flex items-center gap-2', disabled && 'opacity-40')}>
                    <input type="checkbox" className="w-5 h-5" checked={on} disabled={disabled} onChange={() => (on ? remove(p.id) : add(p.id))} />
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: p.color }} />
                    {p.name}
                  </label>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
